import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Clear as ClearIcon,
  DeleteOutline as DeleteIcon,
  PeopleOutlined as PatientsIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import PageHeader from '../components/PageHeader';
import NotificationDialog from '../components/NotificationDialog';
import MyPatientListCard from '../components/myPatients/MyPatientListCard';
import CustomPatientProfileAvatar from '../components/myPatients/CustomPatientProfileAvatar';
import { clientSx } from '../theme';
import {
  createEmptyPatient,
  myPatientPath,
  patientGenderLabel,
  patientLabel,
} from '../utils/myPatients';
import { createMyPatient, deleteMyPatient, listMyPatients } from '../utils/userContentApi';

const PAGE_SIZE = 50;

const MyPatientsHome = () => {
  const theme = useTheme();
  const isMobileLayout = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState('');
  const [appliedQuery, setAppliedQuery] = useState('');
  const [dialog, setDialog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const patientsRef = useRef(patients);
  patientsRef.current = patients;

  useEffect(() => {
    const handle = setTimeout(() => setAppliedQuery(query.trim()), 250);
    return () => clearTimeout(handle);
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listMyPatients({ limit: PAGE_SIZE, offset: 0, q: appliedQuery })
      .then((page) => {
        if (cancelled) return;
        const next = Array.isArray(page?.patients) ? page.patients : [];
        patientsRef.current = next;
        setPatients(next);
        setTotal(page?.total || 0);
      })
      .catch((error) => {
        if (!cancelled) setDialog({ kind: 'error', message: error.message || 'Patients could not be loaded.' });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [appliedQuery]);

  const loadMore = () => {
    if (loadingMore || patientsRef.current.length >= total) return;
    setLoadingMore(true);
    listMyPatients({ limit: PAGE_SIZE, offset: patientsRef.current.length, q: appliedQuery })
      .then((page) => {
        const next = [...patientsRef.current, ...(page?.patients || [])];
        patientsRef.current = next;
        setPatients(next);
        setTotal(page?.total || 0);
      })
      .catch((error) => {
        setDialog({ kind: 'error', message: error.message || 'Patients could not be loaded.' });
      })
      .finally(() => setLoadingMore(false));
  };

  const addPatient = () => {
    const draft = createEmptyPatient();
    createMyPatient(draft)
      .then((saved) => navigate(myPatientPath(saved.id, 'personal')))
      .catch((error) => {
        setDialog({ kind: 'error', message: error.message || 'The patient could not be created.' });
      });
  };

  const removePatient = (id) => {
    deleteMyPatient(id)
      .then(() => {
        const remaining = patientsRef.current.filter((patient) => patient.id !== id);
        patientsRef.current = remaining;
        setPatients(remaining);
        setTotal((count) => Math.max(0, count - 1));
      })
      .catch((error) => {
        setDialog({ kind: 'error', message: error.message || 'The patient could not be removed.' });
      });
  };

  const openPatient = (patient) => {
    navigate(myPatientPath(patient.id, 'personal'));
  };

  const askDelete = (event, id) => {
    if (event) event.stopPropagation();
    const patient = patients.find((item) => item.id === id);
    if (!patient) return;
    setDialog({ kind: 'delete', id, name: patientLabel(patient) });
  };

  const dialogProps = dialog?.kind === 'delete'
    ? {
        open: true,
        title: 'Remove patient?',
        message: `Remove ${dialog.name}? This patient will be deleted.`,
        iconPreset: 'warning',
        buttons: ['reject', 'accept'],
        buttonLabels: { reject: 'Cancel', accept: 'Remove' },
        onAction: {
          accept: () => removePatient(dialog.id),
        },
      }
    : dialog?.kind === 'error'
      ? {
          open: true,
          title: 'Something went wrong',
          message: dialog.message,
          iconPreset: 'warning',
          buttons: ['dismiss'],
        }
      : { open: false, title: '', message: '', buttons: ['dismiss'] };

  const shownPatients = patients;
  const hasMore = shownPatients.length < total;

  return (
    <PageLayout maxWidth="xl">
      <PageHeader
        title="My Patients"
        subtitle="Patients you create are saved to your account."
        sx={{ mb: { xs: 2, md: 4 } }}
        actions={(
          <Button
            type="button"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={addPatient}
          >
            New patient
          </Button>
        )}
      />

      <Paper sx={{ p: { xs: 1, sm: 2, md: 3 }, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search patients..."
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            sx={{ flex: 1, minWidth: { xs: '100%', sm: 280 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: query ? (
                <InputAdornment position="end">
                  <IconButton aria-label="Clear search" size="small" onClick={() => setQuery('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {total === 1 ? '1 patient' : `${total} patients`}
          </Typography>
        </Box>
      </Paper>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">Loading patients…</Typography>
          </Box>
        ) : patients.length === 0 && !appliedQuery ? (
          <Box sx={clientSx.emptyState}>
            <Box
              sx={{
                width: 64,
                height: 64,
                mx: 'auto',
                mb: 2,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'action.hover',
                color: 'primary.main',
              }}
            >
              <PatientsIcon />
            </Box>
            <Typography variant="h6" component="h2" gutterBottom>
              No patients yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 420, mx: 'auto' }}>
              Create a patient to enter personal details, health history, and pathology.
            </Typography>
            <Button type="button" variant="contained" startIcon={<AddIcon />} onClick={addPatient}>
              Create patient
            </Button>
          </Box>
        ) : shownPatients.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No patients match your search.
            </Typography>
          </Box>
        ) : isMobileLayout ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1 }}>
            {shownPatients.map((patient) => (
              <MyPatientListCard
                key={patient.id}
                patient={patient}
                onOpen={openPatient}
                onDelete={(item) => askDelete(null, item.id)}
              />
            ))}
          </Box>
        ) : (
          <TableContainer
            sx={{
              ...clientSx.tableContainer,
              maxHeight: '80vh',
              border: 'none',
              borderRadius: 0,
              '& .MuiTable-root': { tableLayout: 'fixed', width: '100%' },
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '26%' }}>Name</TableCell>
                  <TableCell sx={{ width: '8%' }}>Age</TableCell>
                  <TableCell sx={{ width: '12%' }}>Gender</TableCell>
                  <TableCell sx={{ width: '38%' }}>Chief complaint</TableCell>
                  <TableCell align="right" sx={{ width: '16%', minWidth: 88 }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shownPatients.map((patient) => {
                  const named = Boolean(String(patient.personal.name || '').trim());
                  const age = String(patient.personal?.age || '').trim();
                  const chiefComplaint = String(patient.pathology?.chiefComplaint || '').trim();
                  return (
                    <TableRow
                      key={patient.id}
                      hover
                      onClick={() => openPatient(patient)}
                      sx={{
                        ...clientSx.clickableRow,
                        '& td': { verticalAlign: 'middle' },
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                          <CustomPatientProfileAvatar patient={patient} size={36} />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              color={named ? 'text.primary' : 'text.secondary'}
                              noWrap
                            >
                              {patientLabel(patient)}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {age || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {patientGenderLabel(patient) || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ wordBreak: 'break-word' }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            m: 0,
                          }}
                        >
                          {chiefComplaint || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Box
                          sx={{ display: 'inline-flex', gap: 0.5, justifyContent: 'flex-end' }}
                          onClick={(event) => event.stopPropagation()}
                        >
                          <IconButton
                            size="small"
                            color="error"
                            aria-label={`Remove ${patientLabel(patient)}`}
                            onClick={(event) => askDelete(event, patient.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {hasMore && !loading ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Button type="button" variant="outlined" onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? 'Loading…' : 'Load more'}
            </Button>
          </Box>
        ) : null}
      </Paper>

      <NotificationDialog
        open={dialogProps.open}
        onClose={() => setDialog(null)}
        title={dialogProps.title}
        message={dialogProps.message}
        iconPreset={dialogProps.iconPreset}
        buttons={dialogProps.buttons}
        buttonLabels={dialogProps.buttonLabels}
        onAction={dialogProps.onAction}
      />
    </PageLayout>
  );
};

export default MyPatientsHome;
